import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { EnvelopeIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error('Please enter your email address');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post('/api/auth/forgot-password', { email });

            if (response.data.success) {
                setEmailSent(true);
                toast.success(response.data.message);
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            toast.error(error.response?.data?.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (emailSent) {
        return (
            <div className="min-h-screen bg-white flex items-start justify-center pt-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-sm w-full space-y-6 text-center">
                    {/* Success Icon */}
                    <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-50">
                        <CheckCircleIcon className="h-8 w-8 text-green-600" />
                    </div>

                    {/* Heading and Email */}
                    <div className="space-y-2">
                        <h2 className="text-2xl font-bold text-primary-600">
                            Check Your Email
                        </h2>
                        <div className="space-y-1">
                            <p className="text-gray-500 text-sm">
                                We've sent a password reset link to:
                            </p>
                            <p className="text-gray-900 font-semibold text-base">
                                {email}
                            </p>
                        </div>
                    </div>

                    {/* Instructions - Clean Inline List */}
                    <div className="inline-block text-left text-sm text-green-700 space-y-1.5">
                        <p className="flex items-start gap-2.5">
                            <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-500 mt-0.5" />
                            <span>Check your inbox for the reset email</span>
                        </p>
                        <p className="flex items-start gap-2.5">
                            <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-500 mt-0.5" />
                            <span>Click the reset link (valid for 30 min)</span>
                        </p>
                        <p className="flex items-start gap-2.5">
                            <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-500 mt-0.5" />
                            <span>Create your new password</span>
                        </p>
                        <p className="flex items-start gap-2.5">
                            <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-500 mt-0.5" />
                            <span>Check spam folder if you don't see it</span>
                        </p>
                    </div>

                    {/* Secondary Actions */}
                    <div className="space-y-3 pt-2">
                        <Link
                            to="/login"
                            className="w-10/12 mx-auto flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900 transition-all duration-200"
                        >
                            <ArrowLeftIcon className="h-3 w-3" />
                            Back to Sign In
                        </Link>

                        <button
                            onClick={() => {
                                setEmailSent(false);
                                setEmail('');
                            }}
                            className="text-sm font-normal text-primary-600 hover:text-primary-700 underline transition-colors"
                        >
                            Send to a different email
                        </button>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-white flex items-start justify-center pt-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-sm w-full space-y-6">
                {/* Logo and Header */}
                <div className="text-center">
                    <div className="flex justify-center items-center gap-3 mb-4">
                        <img src="/assets/logo-final.jpg" alt="Bug Tracker Logo" className="h-10 w-10 rounded-full" />
                        <span className="text-xl font-bold text-gray-900">Bug Tracker</span>
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-900">
                        Forgot Password?
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed px-4">
                        Don’t worry! It happens. Please enter the email address associated with your account.
                    </p>
                </div>

                <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                    <div>
                        <label htmlFor="email" className="sr-only">
                            Email Address
                        </label>
                        <div className="relative w-10/12 mx-auto">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="appearance-none rounded-md relative block w-full px-3 py-2 pl-9 border border-gray-300 placeholder-gray-500 text-gray-900 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10"
                                placeholder="Enter your registered email address"
                            />
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <button
                            type="submit"
                            disabled={!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || loading}
                            className={`group relative w-10/12 flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || loading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-primary-600 hover:bg-primary-700 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                                } transition-colors duration-200`}
                        >
                            {loading ? 'Sending...' : 'Request reset link'}
                        </button>
                    </div>

                    <div className="flex justify-center pt-2">
                        <Link
                            to="/login"
                            className="font-medium text-primary-600 hover:text-primary-500 flex items-center gap-2 text-sm"
                        >
                            <ArrowLeftIcon className="h-3 w-3" />
                            Back to Login
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );

};

export default ForgotPassword;
